import paper from 'paper';
import { type fabric } from 'fabric';
import { computeRoundedCornerPath, type CoordsArray } from '.';

/**
 * Converts shape path data to a Paper.js path, scaling coordinates by width and height.
 * Supports standard SVG path commands (M, L, C, Q, Z) plus custom R command for rounded corners.
 */
export const pathDataToPath = (
  pathData: CoordsArray[],
  obj: fabric.Object,
  w: number,
  h: number
): paper.Path => {
  let current: CoordsArray; // current instruction
  let x = 0;
  let y = 0;

  const path = new paper.Path();
  for (let i = 0, len = pathData.length; i < len; ++i) {
    current = pathData[i];

    switch (current[0]) {
      case 'L': // lineto, absolute
        x = current[1];
        y = current[2];
        path.lineTo(new paper.Point(x * w, y * h));
        break;

      case 'M': // moveTo, absolute
        x = current[1];
        y = current[2];
        path.moveTo(new paper.Point(x * w, y * h));
        break;

      case 'C': // bezierCurveTo, absolute
        x = current[5];
        y = current[6];
        path.cubicCurveTo(
          new paper.Point(current[1] * w, current[2] * h),
          new paper.Point(current[3] * w, current[4] * h),
          new paper.Point(current[5] * w, current[6] * h)
        );
        break;

      case 'Q': // quadraticCurveTo, absolute
        x = current[3];
        y = current[4];
        path.quadraticCurveTo(
          new paper.Point(current[1] * w, current[2] * h),
          new paper.Point(current[3] * w, current[4] * h)
        );
        break;

      case 'R': {
        const [aX, aY, bX, bY, cX, cY, dX, dY, eX, eY] =
          computeRoundedCornerPath(
            obj,
            x,
            y,
            current[1],
            current[2],
            current[3]
          );

        path.lineTo(new paper.Point(aX, aY));
        path.cubicCurveTo(
          new paper.Point(bX, bY),
          new paper.Point(cX, cY),
          new paper.Point(dX, dY)
        );
        path.lineTo(new paper.Point(eX, eY));
        x = eX;
        y = eY;
        break;
      }

      case 'z':
      case 'Z':
        path.closePath();
        break;
    }
  }

  return path;
};
